import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import * as RequestPasswordResetStore from '../stores/RequestPasswordResetStore'
import { pageLinks } from '../../pageLinks'

// ============================================================================
// INLINE UI COMPONENTS - self-contained, no external dependencies
// ============================================================================

function Card({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={className} {...props}>
      {children}
    </h3>
  )
}

function CardDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm text-neutral-500 ${className}`} {...props}>
      {children}
    </p>
  )
}

function CardContent({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline'
}

function Button({
  className = '',
  variant = 'default',
  children,
  ...props
}: ButtonProps) {
  const variantStyles =
    variant === 'default'
      ? 'bg-neutral-900 text-white'
      : 'bg-neutral-100 text-neutral-900'

  return (
    <button
      className={`inline-flex items-center justify-center text-sm h-10 px-4 py-2 disabled:opacity-50 disabled:pointer-events-none ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

function Input({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`flex h-10 w-full border-b border-neutral-200 bg-transparent px-0 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 disabled:opacity-50 ${className}`}
      {...props}
    />
  )
}

function Label({
  className = '',
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={`text-sm text-neutral-500 ${className}`}
      {...props}
    >
      {children}
    </label>
  )
}

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive'
}

function Alert({
  className = '',
  variant = 'default',
  children,
  ...props
}: AlertProps) {
  const variantStyles =
    variant === 'destructive'
      ? 'text-red-600'
      : 'text-neutral-900'

  return (
    <div
      role="alert"
      className={`text-sm ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

function AlertDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm ${className}`} {...props}>
      {children}
    </p>
  )
}

// ============================================================================
// FIELD COMPONENTS - directly connected to Jotai store
// ============================================================================

function EmailField() {
  const { email } = useAtomValue(
    RequestPasswordResetStore.requestPasswordResetFormAtom,
  )
  const errors = useAtomValue(
    RequestPasswordResetStore.requestPasswordResetFormErrorsAtom,
  )
  const updateForm = useSetAtom(
    RequestPasswordResetStore.updateRequestPasswordResetFormAtom,
  )

  return (
    <div className="space-y-1">
      <Label htmlFor="email">Email address</Label>
      <Input
        id="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => updateForm({ email: e.target.value })}
        placeholder="Enter your email"
        aria-invalid={!!errors?.email}
      />
      {errors?.email && (
        <p className="text-xs text-red-600 mt-1">{errors.email}</p>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function RequestPasswordReset() {
  const navigate = useNavigate()
  const { isSubmitting } = useAtomValue(
    RequestPasswordResetStore.requestPasswordResetFormAtom,
  )
  const errors = useAtomValue(
    RequestPasswordResetStore.requestPasswordResetFormErrorsAtom,
  )
  const isSuccess = useAtomValue(
    RequestPasswordResetStore.requestPasswordResetSuccessAtom,
  )
  const submitRequestPasswordReset = useSetAtom(
    RequestPasswordResetStore.submitRequestPasswordResetAtom,
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitRequestPasswordReset()
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="text-center space-y-1 pb-6">
            <CardTitle className="text-xl text-neutral-900">
              {isSuccess ? 'Check your inbox' : 'Password Reset'}
            </CardTitle>
            <CardDescription>
              {isSuccess
                ? "We've sent a reset link to your email address."
                : "Enter your email below and we'll send you instructions to reset your password."}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isSuccess ? (
              <div className="space-y-6">
                <p className="text-sm text-neutral-500">
                  A recovery link has been sent. Please check your inbox and
                  spam folder.
                </p>

                <Button
                  onClick={() => navigate(pageLinks.global_shortcut_login())}
                  className="w-full"
                >
                  Return to login
                </Button>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {errors?.form && (
                  <Alert variant="destructive">
                    <AlertDescription>{errors.form}</AlertDescription>
                  </Alert>
                )}

                <EmailField />

                <div className="space-y-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Sending Link...' : 'Send Reset Link'}
                  </Button>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(pageLinks.global_shortcut_login())
                      }
                      className="text-sm text-neutral-400 hover:text-neutral-900"
                    >
                      Back to login
                    </button>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
